import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createScenarioSnapshotAPI,
  fetchScenarioSnapshotsAPI,
  refreshScenarioSnapshotAPI,
} from '../../services/api/scenarioSnapshotsService';
import { ScenarioSnapshot } from '../../types/snapshots/snapshot.type';
import { SnapshotScenarioDetailsInput } from 'src/API';

// ============================================================================
// Type Definitions
// ============================================================================

interface ScenarioSnapshotsState {
  data: ScenarioSnapshot[];
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

interface ScenarioSnapshotsSliceState {
  scenarioSnapshots: ScenarioSnapshotsState;
  selectedSnapshot: {
    data: ScenarioSnapshot | null;
    isLoading: boolean;
    error: string | null;
  };
}

interface FetchScenarioSnapshotsParams {
  forceRefresh?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// Cache timeout: 5 minutes in milliseconds
const CACHE_TIMEOUT_MS = 5 * 60 * 1000;

// ============================================================================
// Initial State
// ============================================================================

const initialState: ScenarioSnapshotsSliceState = {
  scenarioSnapshots: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  selectedSnapshot: {
    data: null,
    isLoading: false,
    error: null,
  },
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetches all scenario snapshots with optional filtering
 */
export const fetchScenarioSnapshots = createAsyncThunk(
  'scenarioSnapshots/fetchScenarioSnapshots',
  async (params: FetchScenarioSnapshotsParams = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { scenarioSnapshots: ScenarioSnapshotsSliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.scenarioSnapshots.scenarioSnapshots;

      // Check if we should skip fetch (unless forced refresh)
      if (!params.forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data
          return state.scenarioSnapshots.scenarioSnapshots.data;
        }
      }

      // Fetch data from API
      const data = await fetchScenarioSnapshotsAPI();

      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch scenario snapshots');
    }
  }
);

export const createScenarioSnapshot = createAsyncThunk(
  'scenarioSnapshots/createScenarioSnapshot',
  async (snapshot: SnapshotScenarioDetailsInput, { rejectWithValue }) => {
    try {
      return await createScenarioSnapshotAPI(snapshot);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to create snapshot');
    }
  }
);

export const refreshScenarioSnapshot = createAsyncThunk(
  'scenarioSnapshots/refreshScenarioSnapshot',
  async (snapshot: SnapshotScenarioDetailsInput, { rejectWithValue }) => {
    try {
      return await refreshScenarioSnapshotAPI(snapshot);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to create snapshot');
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const scenarioSnapshotsSlice = createSlice({
  name: 'scenarioSnapshots',
  initialState,
  reducers: {
    /**
     * Clears any error messages
     */
    clearError: (state) => {
      state.scenarioSnapshots.error = null;
      state.selectedSnapshot.error = null;
    },

    /**
     * Clears the selected snapshot error
     */
    clearSelectedSnapshotError: (state) => {
      state.selectedSnapshot.error = null;
    },

    /**
     * Resets the fetch status flags
     */
    resetFetchStatus: (state) => {
      state.scenarioSnapshots.hasAttemptedFetch = false;
      state.scenarioSnapshots.hasSuccessfulFetch = false;
      state.scenarioSnapshots.lastFetchTimestamp = null;
    },

    /**
     * Clears the selected snapshot data
     */
    clearSelectedSnapshot: (state) => {
      state.selectedSnapshot.data = null;
      state.selectedSnapshot.error = null;
    },

    /**
     * Sets the selected snapshot manually
     */
    setSelectedSnapshot: (state, action: PayloadAction<ScenarioSnapshot>) => {
      state.selectedSnapshot.data = action.payload;
      state.selectedSnapshot.error = null;
    },

    /**
     * Resets all state to initial values
     */
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchScenarioSnapshots
      .addCase(fetchScenarioSnapshots.pending, (state) => {
        state.scenarioSnapshots.isLoading = true;
        state.scenarioSnapshots.error = null;
      })
      .addCase(fetchScenarioSnapshots.fulfilled, (state, action) => {
        state.scenarioSnapshots.isLoading = false;
        state.scenarioSnapshots.data = action.payload;
        state.scenarioSnapshots.hasAttemptedFetch = true;
        state.scenarioSnapshots.hasSuccessfulFetch = true;
        state.scenarioSnapshots.lastFetchTimestamp = Date.now();
        state.scenarioSnapshots.error = null;
      })
      .addCase(fetchScenarioSnapshots.rejected, (state, action) => {
        state.scenarioSnapshots.isLoading = false;
        state.scenarioSnapshots.error = action.payload as string;
        state.scenarioSnapshots.hasAttemptedFetch = true;
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

// Export actions
export const {
  clearError,
  clearSelectedSnapshotError,
  resetFetchStatus,
  clearSelectedSnapshot,
  setSelectedSnapshot,
  reset,
} = scenarioSnapshotsSlice.actions;

// Export reducer
export default scenarioSnapshotsSlice.reducer;
